import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Content Radar filters live in the query string so a filtered view is shareable, reloadable and
 * works with Back/Forward. Only non-default values are written (a default view has a clean URL),
 * and parsing is defensive: unknown/invalid values fall back to the defaults.
 *
 * Scheme (both tabs; each tab only reads its own keys):
 *   q=<text>                  free-text search
 *   entry=…&issue=…&category=… repeated keys (values can contain commas, so no CSV)
 *   sort=<field>&order=asc|desc, or sort=none for "unsorted" where the tab has a default sort
 *   page=<n>                  1-based, omitted for page 1
 * Clusters tab also: country=…, geo=local|regional|international, count=<5|5-9|10-14|15+, rec=1
 * Articles tab also: kind=custom|relevant, source=…
 *
 * The open cluster is a path segment (/radar/clusters/:clusterId, see appRoutes), and the
 * analytic profile is deliberately NOT in the URL (per-user preference in localStorage).
 */

export const RADAR_SORT_FIELDS = ['published_at', 'article_count', 'country'] as const;
export type RadarUrlSortField = (typeof RADAR_SORT_FIELDS)[number];
export type RadarUrlSort = { field: RadarUrlSortField; order: 'asc' | 'desc' } | null;

export const GEO_SCOPES = ['local', 'regional', 'international'] as const;
export type GeoScope = (typeof GEO_SCOPES)[number];

export const ARTICLE_COUNT_BUCKETS = ['<5', '5-9', '10-14', '15+'] as const;
export type ArticleCountBucket = (typeof ARTICLE_COUNT_BUCKETS)[number];

export const ARTICLE_KINDS = ['all', 'custom', 'relevant'] as const;
export type ArticleKindFilter = (typeof ARTICLE_KINDS)[number];

const MAX_LIST_VALUES = 50;
const MAX_VALUE_LENGTH = 200;
const MAX_PAGE = 10_000;

type Common = {
  q: string;
  entryTypes: string[];
  issueTypes: string[];
  contentCategories: string[];
  sort: RadarUrlSort;
  page: number;
};

export type RadarClustersFilters = Common & {
  countries: string[];
  geoScopes: GeoScope[];
  articleCounts: ArticleCountBucket[];
  recommended: boolean;
};

export type RadarArticlesFilters = Common & {
  kind: ArticleKindFilter;
  sources: string[];
};

export const CLUSTERS_DEFAULT_FILTERS: RadarClustersFilters = {
  q: '',
  countries: [],
  geoScopes: [],
  articleCounts: [],
  entryTypes: [],
  issueTypes: [],
  contentCategories: [],
  recommended: false,
  sort: { field: 'published_at', order: 'desc' },
  page: 1,
};

export const ARTICLES_DEFAULT_FILTERS: RadarArticlesFilters = {
  q: '',
  kind: 'all',
  sources: [],
  entryTypes: [],
  issueTypes: [],
  contentCategories: [],
  sort: null,
  page: 1,
};

// ---- parsing helpers ----

const toParams = (search: string | URLSearchParams) =>
  typeof search === 'string' ? new URLSearchParams(search) : search;

const parseText = (p: URLSearchParams, key: string) => (p.get(key) ?? '').trim().slice(0, MAX_VALUE_LENGTH);

/** Repeated key → trimmed, non-empty, de-duplicated values (order kept). */
const parseList = (p: URLSearchParams, key: string): string[] => {
  const out: string[] = [];
  for (const raw of p.getAll(key)) {
    const v = raw.trim();
    if (v && v.length <= MAX_VALUE_LENGTH && !out.includes(v)) out.push(v);
    if (out.length >= MAX_LIST_VALUES) break;
  }
  return out;
};

const parseEnumList = <T extends string>(p: URLSearchParams, key: string, allowed: readonly T[]): T[] =>
  parseList(p, key).filter((v): v is T => (allowed as readonly string[]).includes(v));

const parsePage = (p: URLSearchParams) => {
  const raw = p.get('page');
  if (!raw || !/^\d+$/.test(raw)) return 1;
  const n = Number(raw);
  return n >= 1 && n <= MAX_PAGE ? n : 1;
};

const parseSort = (p: URLSearchParams, fallback: RadarUrlSort): RadarUrlSort => {
  const field = p.get('sort');
  if (field === 'none') return null;
  if (!field || !(RADAR_SORT_FIELDS as readonly string[]).includes(field)) return fallback;
  const order = p.get('order');
  return { field: field as RadarUrlSortField, order: order === 'asc' ? 'asc' : 'desc' };
};

const sameSort = (a: RadarUrlSort, b: RadarUrlSort) =>
  a === b || (!!a && !!b && a.field === b.field && a.order === b.order);

const parseCommon = (p: URLSearchParams, defaults: Common): Common => ({
  q: parseText(p, 'q'),
  entryTypes: parseList(p, 'entry'),
  issueTypes: parseList(p, 'issue'),
  contentCategories: parseList(p, 'category'),
  sort: parseSort(p, defaults.sort),
  page: parsePage(p),
});

// ---- serializing helpers ----

const writeCommon = (p: URLSearchParams, f: Common, defaults: Common) => {
  const q = f.q.trim();
  if (q) p.set('q', q);
  f.entryTypes.forEach((v) => p.append('entry', v));
  f.issueTypes.forEach((v) => p.append('issue', v));
  f.contentCategories.forEach((v) => p.append('category', v));
  if (!sameSort(f.sort, defaults.sort)) {
    if (f.sort) {
      p.set('sort', f.sort.field);
      p.set('order', f.sort.order);
    } else {
      p.set('sort', 'none');
    }
  }
  if (f.page > 1) p.set('page', String(f.page));
};

const toSearch = (p: URLSearchParams) => {
  const s = p.toString();
  return s ? `?${s}` : '';
};

// ---- public API ----

export const parseClustersFilters = (search: string | URLSearchParams): RadarClustersFilters => {
  const p = toParams(search);
  return {
    ...parseCommon(p, CLUSTERS_DEFAULT_FILTERS),
    countries: parseList(p, 'country'),
    geoScopes: parseEnumList(p, 'geo', GEO_SCOPES),
    articleCounts: parseEnumList(p, 'count', ARTICLE_COUNT_BUCKETS),
    recommended: p.get('rec') === '1',
  };
};

/** Returns '' for the default view, else a '?…' query string. */
export const serializeClustersFilters = (f: RadarClustersFilters): string => {
  const p = new URLSearchParams();
  writeCommon(p, f, CLUSTERS_DEFAULT_FILTERS);
  f.countries.forEach((v) => p.append('country', v));
  f.geoScopes.forEach((v) => p.append('geo', v));
  f.articleCounts.forEach((v) => p.append('count', v));
  if (f.recommended) p.set('rec', '1');
  return toSearch(p);
};

export const parseArticlesFilters = (search: string | URLSearchParams): RadarArticlesFilters => {
  const p = toParams(search);
  const kind = p.get('kind');
  return {
    ...parseCommon(p, ARTICLES_DEFAULT_FILTERS),
    kind: kind && (ARTICLE_KINDS as readonly string[]).includes(kind) ? (kind as ArticleKindFilter) : 'all',
    sources: parseList(p, 'source'),
  };
};

export const serializeArticlesFilters = (f: RadarArticlesFilters): string => {
  const p = new URLSearchParams();
  writeCommon(p, f, ARTICLES_DEFAULT_FILTERS);
  if (f.kind !== 'all') p.set('kind', f.kind);
  f.sources.forEach((v) => p.append('source', v));
  return toSearch(p);
};

/** Tri-state column sort used by both Radar tables: asc → desc → unsorted → asc. */
export const nextSort = (current: RadarUrlSort, field: RadarUrlSortField): RadarUrlSort => {
  if (!current || current.field !== field) return { field, order: 'asc' };
  return current.order === 'asc' ? { field, order: 'desc' } : null;
};

/** Toggle a value in a list filter (adds at the end, removes in place). */
export const toggleValue = <T>(list: readonly T[], value: T): T[] =>
  list.includes(value) ? list.filter((v) => v !== value) : [...list, value];

export type RadarFilterUpdateOptions = {
  /** Replace the current history entry instead of pushing (used for search keystrokes). */
  replace?: boolean;
  /** Keep the current page (default: any filter change goes back to page 1). */
  keepPage?: boolean;
  /** Target pathname (defaults to the current one). */
  pathname?: string;
};

/**
 * URL-backed filter state for a Radar tab. The query string is the source of truth; `update`
 * navigates (push by default) and resets the page unless the patch sets one or keepPage is set.
 */
export function useRadarUrlFilters<F extends { page: number }>(
  parse: (search: string) => F,
  serialize: (f: F) => string,
) {
  const location = useLocation();
  const navigate = useNavigate();
  const filters = useMemo(() => parse(location.search), [parse, location.search]);

  // Debounced callers (search box) must patch the latest URL, not the one from when they were scheduled.
  const latest = useRef({ filters, pathname: location.pathname, search: location.search });
  latest.current = { filters, pathname: location.pathname, search: location.search };

  const update = useCallback(
    (patch: Partial<F>, opts: RadarFilterUpdateOptions = {}) => {
      const cur = latest.current;
      const next = { ...cur.filters, ...patch } as F;
      if (!('page' in patch) && !opts.keepPage) next.page = 1;
      const search = serialize(next);
      const pathname = opts.pathname ?? cur.pathname;
      if (pathname === cur.pathname && search === cur.search) return;
      navigate({ pathname, search }, { replace: !!opts.replace });
    },
    [navigate, serialize],
  );

  return { filters, update, location, navigate };
}

/**
 * Search box bound to the `q` param: the input updates immediately and the URL follows after a short
 * debounce. Starting a search (empty → text) pushes one history entry so Back returns to the
 * unsearched list; refining or clearing it replaces that entry (no entry per keystroke).
 * Back/Forward (an external change of `q`) resets the input.
 */
export function useRadarSearchInput(
  urlQuery: string,
  setUrlQuery: (q: string, opts: { replace: boolean }) => void,
  delayMs = 250,
): [string, (value: string) => void] {
  const [value, setValue] = useState(urlQuery);
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (valueRef.current.trim() !== urlQuery) setValue(urlQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to URL changes
  }, [urlQuery]);

  const setUrlQueryRef = useRef(setUrlQuery);
  setUrlQueryRef.current = setUrlQuery;
  const urlQueryRef = useRef(urlQuery);
  urlQueryRef.current = urlQuery;
  useEffect(() => {
    const id = window.setTimeout(() => {
      const trimmed = value.trim();
      const current = urlQueryRef.current;
      if (trimmed !== current) setUrlQueryRef.current(trimmed, { replace: current !== '' });
    }, delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);

  return [value, setValue];
}

